import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowUpRight, Info, Phone, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePageSEO } from "@/lib/seo";

const onderwerpen = [
  { key: "toeslagenaffaire", label: "Toeslagenaffaire / herstel" },
  { key: "geldzorgen", label: "Geldzorgen" },
  { key: "wonen", label: "Wonen / dreigende dakloosheid" },
  { key: "administratie", label: "Administratie / instanties" },
  { key: "gezin", label: "Gezin / praktische problemen" },
  { key: "anders", label: "Anders" },
];

export default function HulpNodigPage() {
  usePageSEO({
    title: "Hulp nodig? | Stichting FIN",
    description:
      "Je hoeft niet eerst alles op orde te hebben om hulp te vragen. Vraag laagdrempelig een gesprek aan bij Stichting FIN — vrijblijvend en zonder kosten.",
  });

  const [form, setForm] = useState({
    naam: "",
    telefoon: "",
    email: "",
    woonplaats: "",
    onderwerp: "",
    bericht: "",
    privacy: false,
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Bevestig dat u akkoord bent met de privacyverklaring.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        type: "hulp",
        naam: form.naam,
        email: form.email,
        telefoon: form.telefoon || undefined,
        woonplaats: form.woonplaats || undefined,
        thema: form.onderwerp || undefined,
        bericht: form.bericht,
      });
      toast.success("Bedankt — we nemen zo snel mogelijk contact met u op.");
      setForm({ naam: "", telefoon: "", email: "", woonplaats: "", onderwerp: "", bericht: "", privacy: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="hulp-nodig-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Hulp nodig?</p>
          <h1 className="font-display text-[clamp(2.2rem,5.5vw,4.4rem)] leading-[1.02] tracking-tight text-balance">
            Je hoeft niet eerst alles op orde te hebben om <em className="italic text-gold">hulp te vragen</em>.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            Vul onderstaand formulier in — vrijblijvend en zonder kosten. Wij nemen zo snel mogelijk contact met u op om te
            luisteren en samen te kijken wat er speelt.
          </p>
        </div>
      </section>

      {/* Info bar */}
      <section className="py-6 bg-goldSoft border-y border-line">
        <div className="container-x flex items-start gap-3">
          <Info size={18} className="text-goldDark mt-0.5 shrink-0" />
          <p className="text-petrol text-[14.5px] leading-relaxed">
            <span className="font-semibold">Niet bedoeld voor acute noodsituaties.</span> Bel bij levensbedreiging altijd 112.
            Bij crisis kunt u ook 24/7 terecht bij <a className="underline hover:text-goldDark" href="https://www.113.nl" target="_blank" rel="noopener noreferrer">113 Zelfmoordpreventie</a>.
          </p>
        </div>
      </section>

      {/* Formulier + info */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-ivory border border-line rounded-3xl p-6 md:p-8 shadow-soft space-y-4"
              data-testid="hulp-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Naam" name="naam" value={form.naam} onChange={onChange} required testId="hulp-naam" />
                <Field label="Telefoonnummer" name="telefoon" type="tel" value={form.telefoon} onChange={onChange} testId="hulp-telefoon" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="E-mailadres" type="email" name="email" value={form.email} onChange={onChange} required testId="hulp-email" />
                <Field label="Woonplaats" name="woonplaats" value={form.woonplaats} onChange={onChange} testId="hulp-woonplaats" />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">
                  Waar kunnen we bij helpen? *
                </label>
                <select
                  name="onderwerp"
                  value={form.onderwerp}
                  onChange={onChange}
                  required
                  className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
                  data-testid="hulp-onderwerp"
                >
                  <option value="">Kies een onderwerp…</option>
                  {onderwerpen.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">
                  Wat speelt er op dit moment? *
                </label>
                <textarea
                  name="bericht"
                  value={form.bericht}
                  onChange={onChange}
                  required
                  rows={6}
                  className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
                  placeholder="Vertel gerust in eigen woorden. U hoeft geen details te delen die u niet wilt delen."
                  data-testid="hulp-bericht"
                />
                <p className="mt-2 text-[12px] text-inkMuted/80 italic">
                  Deel via dit formulier geen BSN, dossiers of medische informatie — dat regelen we later via een veilige route.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer text-[14px] text-inkMuted" data-testid="hulp-privacy-label">
                <input
                  type="checkbox"
                  name="privacy"
                  checked={form.privacy}
                  onChange={onChange}
                  className="mt-1 h-4 w-4 rounded border-line text-gold focus:ring-gold"
                  data-testid="hulp-privacy"
                />
                <span>
                  Ik ga akkoord met de <Link to="/privacy" className="underline text-petrol hover:text-gold">privacyverklaring</Link> en geef toestemming aan Stichting FIN om contact met mij op te nemen.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-petrol hover:bg-goldSoft disabled:opacity-60 disabled:cursor-not-allowed transition rounded-full px-6 py-3.5 text-sm font-semibold shadow-gold"
                data-testid="hulp-submit"
              >
                {loading ? "Versturen…" : "Vraag een gesprek aan"} <ArrowUpRight size={16} />
              </button>
            </form>
          </div>

          <aside className="lg:col-span-5 space-y-4">
            <div className="bg-sandSoft border border-line rounded-3xl p-7">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold mb-4">Waar u op kunt rekenen</p>
              <ul className="space-y-3 text-[15px] text-inkMuted leading-relaxed">
                <li className="flex items-start gap-3">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mt-2.5 shrink-0" />
                  <span>Uw bericht komt bij één van onze medewerkers — niet in een algemene bak.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mt-2.5 shrink-0" />
                  <span>Deelname is <strong className="text-petrol">gratis en vrijblijvend</strong>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mt-2.5 shrink-0" />
                  <span>U bepaalt zelf welk tempo u wilt aanhouden en wat u wilt delen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mt-2.5 shrink-0" />
                  <span>Als een andere organisatie beter past, verwijzen we netjes door.</span>
                </li>
              </ul>
            </div>

            <div className="bg-petrol text-cream rounded-3xl p-7 relative overflow-hidden">
              <div aria-hidden="true" className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/20 blur-3xl" />
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold mb-4 relative">Direct contact</p>
              <div className="space-y-3 relative">
                <a href="tel:+31643828430" className="flex items-center gap-3 text-cream hover:text-gold transition">
                  <Phone size={17} className="text-gold" /> +31 6 43 82 84 30
                </a>
                <a href="mailto:info@stichting-fin.nl" className="flex items-center gap-3 text-cream hover:text-gold transition">
                  <Mail size={17} className="text-gold" /> info@stichting-fin.nl
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
        data-testid={testId}
      />
    </div>
  );
}
